import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  FileEdit, Activity, Home,
  FileText, FileBarChart, Shield, Baby,
  ChevronDown, ChevronUp, Heart, CheckCircle,
  ClipboardList, LogOut, RefreshCw, Move, Target,
  Footprints, Gauge, Zap, AlertTriangle,
  ChevronsUp, ChevronsDown
} from 'lucide-react';
import PrimaryButton from '../../common/PrimaryButton';
import { useFeature } from '../../../hooks/useFeature';
import type { AssessmentFormHandle } from './NursingAssessmentForm';
import AdultTransitionToggle from './AdultTransitionToggle';
import { withPediatricFirstConfig, patientTypeLabel } from './assessmentFormConfig';
import ICDSelect from '../../common/ICDSelect';
import { joinIcd10Field, splitIcd10Field } from '../../../utils/icd10Entries';
import { StandardizedTestSection } from './StandardizedTestTable';
import { legacyEntries, pickTherapyTestFields, therapyTestErrors } from './standardizedTests';
import type { TherapyTestFieldsDto } from '../../../dtos/patientmr/assessments/therapy-tests.dto';

// Phase 19 F23: the old free-text test fields; legacy values show read-only as "Earlier entry"
const LEGACY_TEST_KEYS = [
  { key: 'Pediatric Motor Development-Standardized Assessment Tool Used', label: 'Standardized Assessment Tool Used' },
  { key: 'Pediatric Motor Development-Assessment Score/Percentile', label: 'Assessment Score/Percentile' },
];

// Assessment Type definitions
type AssessmentType = 'initial' | 'recertification' | 'discharge';

interface AssessmentTypeConfig {
  label: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

interface FieldConfig {
  name: string;
  type: 'text' | 'textarea' | 'select' | 'date' | 'checkbox-group' | 'number';
  required?: boolean;
  placeholder?: string;
  options?: string[];
  showFor?: AssessmentType[];
  readonly?: boolean;
  min?: number;
  max?: number;
}

const ASSESSMENT_TYPES: Record<AssessmentType, AssessmentTypeConfig> = {
  initial: {
    label: 'Initial/Admission Evaluation (SOC)',
    description: 'Comprehensive PT evaluation for Start of Care. All baseline data collected.',
    icon: <ClipboardList size={16} />,
    color: 'blue'
  },
  recertification: {
    label: 'Recertification Evaluation',
    description: 'Periodic reassessment (60-90 days). Focus on progress and continued medical necessity.',
    icon: <RefreshCw size={16} />,
    color: 'amber'
  },
  discharge: {
    label: 'Discharge Evaluation',
    description: 'Final evaluation documenting outcomes, functional status, and recommendations.',
    icon: <LogOut size={16} />,
    color: 'green'
  }
};

interface PTAssessmentFormProps {
  editingId: string | null;
  patientId: string;
  onSave: (data: any) => void;
  existingData?: any;
  assessmentStatus?: string;
  isReadOnly?: boolean;
}

const PTAssessmentForm = forwardRef<AssessmentFormHandle, PTAssessmentFormProps>(
  ({ editingId, patientId, onSave, existingData = null, assessmentStatus, isReadOnly = false }, ref) => {
  // Phase 3: Medicare content (face-to-face, Medicare wording) is hidden unless the medicareContent flag is on
  const medicareEnabled = useFeature('medicareContent');
  // Default form configuration
  const defaultFormConfig = {
    assessmentType: 'initial' as AssessmentType,
    patientType: 'pediatric', // Phase 19 F26: 'pediatric' or 'adult' (transition, 18–21 toggle)
    settingType: 'home-health', // Phase 19 F26: always home health (no selector)
    showOptionalSections: false
  };

  // Helper to normalize assessment type from backend format to internal format
  const normalizeAssessmentType = (type: string): AssessmentType => {
    const typeMap: Record<string, AssessmentType> = {
      'START_OF_CARE': 'initial',
      'start_of_care': 'initial',
      'initial': 'initial',
      'REASSESSMENT': 'recertification',
      'reassessment': 'recertification',
      'recertification': 'recertification',
      'END_OF_CARE': 'discharge',
      'end_of_care': 'discharge',
      'discharge': 'discharge'
    };
    return typeMap[type] || 'initial';
  };

  // Form configuration state - MVP defaults to pediatric for home health
  const [formConfig, setFormConfig] = useState(() => {
    // If editing existing data with formConfig, use that immediately
    if (existingData && existingData.formConfig) {
      const config = withPediatricFirstConfig({ ...defaultFormConfig, ...existingData.formConfig });
      // Normalize assessment type from backend format to internal format
      config.assessmentType = normalizeAssessmentType(config.assessmentType);
      return config;
    }
    // Otherwise use defaults for new form
    return defaultFormConfig;
  });

  // Medical codes state
  const [secondaryICD10Codes, setSecondaryICD10Codes] = useState<string[]>([]);
  const [selectedSecondaryICD10, setSelectedSecondaryICD10] = useState<string>('');

  // Track if initial data has been loaded to prevent overwriting
  const initialLoadCompleteRef = useRef(false);

  // Collapsible sections state
  const [configSectionCollapsed, setConfigSectionCollapsed] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({
    'Recertification Summary': true,
    'Discharge Summary': true,
    'Diagnosis and Coding': true,
    'Functional Limitations': true,
    'Home Health Certification': true,
    'Physician Orders and Certification': true,
    'Patient History': true,
    'Mental Status': true,
    'Activities Permitted': true,
    'Pediatric Information': true,
    'Pediatric Motor Development': true,
    'Pediatric PDN Physical Therapy': true,
    'Pain Assessment': true,
    'Range of Motion': true,
    'Strength Assessment': true,
    'Balance & Coordination': true,
    'Gait Analysis': true,
    'Functional Mobility': true,
    'Equipment & Assistive Devices': true,
    'Home Safety Assessment': true,
    'Treatment Plan': true,
    'PT Attestation': true,
    'Standardized Tests': true
  });

  // Track if all sections are collapsed for toggle button
  const [, setAllSectionsCollapsed] = useState(true);

  useEffect(() => {
    // Dynamically check all currently rendered categories
    const allCategoryNames = getAssessmentCategories().map(cat => cat.name);
    const allCollapsed = allCategoryNames.every(
      (name) => collapsedSections[name] === true
    );
    setAllSectionsCollapsed(allCollapsed);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- getAssessmentCategories is recreated every render; the listed deps are the inputs that change the categories
  }, [collapsedSections, formConfig]);


  // Listen for toggle all sections events from parent
  useEffect(() => {
    const handleToggleAll = (event: any) => {
      toggleAllSections(event.detail.collapse);
    };
    window.addEventListener('toggleAllSections', handleToggleAll);
    return () => window.removeEventListener('toggleAllSections', handleToggleAll);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- toggleAllSections is recreated every render; the listener is re-registered when collapsedSections changes
  }, [collapsedSections]);



  // Helper to check if a field should be visible based on assessment type
  const shouldShowField = (field: FieldConfig): boolean => {
    if (!field.showFor) return true;
    return field.showFor.includes(formConfig.assessmentType);
  };

  // Helper to get dynamic placeholder based on assessment type
  const getPlaceholder = (field: FieldConfig): string => {
    const { assessmentType } = formConfig;
    
    if (assessmentType === 'recertification') {
      const recertPlaceholders: Record<string, string> = {
        'Chief Complaint': 'Current primary concerns or changes since last evaluation',
        'History of Present Illness': 'Update on current condition and any changes since last evaluation',
        'Prior Level of Function': 'Document any changes to prior level of function since last evaluation',
        'Functional Limitations': 'Current functional limitations - note any changes from baseline',
        'Treatment Goals': 'Updated goals for this certification period',
        'Recommended PT Frequency': 'e.g., Continue 2x/week OR Change to 1x/week',
        'Frequency Justification': 'Justify continued or changed frequency based on current patient status'
      };
      if (recertPlaceholders[field.name]) return recertPlaceholders[field.name];
    }
    
    if (assessmentType === 'discharge') {
      const dischargePlaceholders: Record<string, string> = {
        'Functional Limitations': 'Functional status at discharge compared to admission',
        'Treatment Goals': 'Final goal status - Met/Partially Met/Not Met for each goal',
        'Recommended PT Frequency': 'N/A - Discharge',
        'PT Summary': 'Summary of interventions provided and patient response during episode of care'
      };
      if (dischargePlaceholders[field.name]) return dischargePlaceholders[field.name];
    }
    
    return field.placeholder || `Enter ${field.name}...`;
  };

  // Get assessment categories based on configuration
  const getAssessmentCategories = () => {
    const categories = [];
    const { assessmentType } = formConfig;

    // ========== RECERTIFICATION SUMMARY (only for recert) ==========
    if (assessmentType === 'recertification') {
      categories.push({
        name: "Recertification Summary",
        priority: 0.5,
        required: true,
        description: "Document progress since last evaluation and justification for continued PT services.",
        fields: [
          { name: "Previous Evaluation Date", type: "date", required: true },
          { name: "Recertification Period Start", type: "date", required: true },
          { name: "Recertification Period End", type: "date", required: true },
          { name: "Changes Since Last Evaluation", type: "textarea", required: true,
            placeholder: "Document all changes in patient condition, functional status, equipment, or living situation since the last PT evaluation." },
          { name: "Progress Toward Goals", type: "textarea", required: true,
            placeholder: "For each treatment goal, document:\n- Goal description\n- Baseline measurement\n- Current measurement\n- Progress status (Met, Partially Met, Not Met, Ongoing)" },
          { name: "Continued Medical Necessity Justification", type: "textarea", required: true,
            placeholder: "Explain why continued skilled PT services are medically necessary. Include: ongoing functional deficits, skilled care requirements, safety concerns, and expected outcomes." },
          { name: "Barriers to Progress", type: "textarea",
            placeholder: "Document any barriers to achieving goals (medical complications, compliance issues, equipment delays, etc.)" },
          { name: "Plan Modifications", type: "textarea",
            placeholder: "Document any changes to treatment plan, goals, or frequency for the next certification period" },
          { name: "Physician Contact for Recertification", type: "select", required: true,
            options: ["Physician contacted - verbal orders obtained", "Physician contacted - awaiting response", "Face-to-face encounter completed", "Telehealth encounter completed", "Orders received via fax/portal"] },
          { name: "Physician Contact Date", type: "date" },
          { name: "Updated Orders Received", type: "select",
            options: ["No changes to orders", "Orders updated - see details", "Pending physician signature"] },
          { name: "Order Changes Description", type: "textarea", placeholder: "If orders were updated, describe the changes" }
        ]
      });
    }

    // ========== DISCHARGE SUMMARY (only for discharge) ==========
    if (assessmentType === 'discharge') {
      categories.push({
        name: "Discharge Summary",
        priority: 0.5,
        required: true,
        description: "Complete discharge documentation including outcomes, recommendations, and follow-up plans.",
        fields: [
          { name: "Discharge Date", type: "date", required: true },
          { name: "Original Start of Care Date", type: "date", required: true },
          { name: "Total Visits Provided", type: "text", placeholder: "e.g., 24 visits over 6 months" },
          { name: "Discharge Reason", type: "select", required: true,
            options: ["Goals met - PT no longer needed", "Goals partially met - maximum benefit achieved", "Patient/family request", "Moved out of service area", 
                      "Transferred to outpatient PT", "Transferred to another agency", "Hospitalization", "Medical decline - PT on hold",
                      "Non-compliance with treatment", "Physician discharged from PT", "Insurance/authorization ended", "Patient deceased", "Other - see notes"] },
          { name: "Discharge Reason Details", type: "textarea", placeholder: "Provide additional details about the discharge reason" },
          { name: "Goals Met Summary", type: "textarea", required: true,
            placeholder: "For each treatment goal, document final status:\n- Goal 1: [Met/Partially Met/Not Met] - Baseline: ___ Current: ___\n- Goal 2: [Met/Partially Met/Not Met] - Baseline: ___ Current: ___" },
          { name: "Functional Outcomes Summary", type: "textarea", required: true,
            placeholder: "Compare functional status at discharge to admission:\n- Mobility: Admission ___ → Discharge ___\n- Transfers: Admission ___ → Discharge ___\n- Balance: Admission ___ → Discharge ___" },
          { name: "Discharge Functional Status", type: "select", required: true,
            options: ["Independent in all mobility", "Modified independent", "Supervision required", "Minimal assistance", "Moderate assistance", "Maximum assistance", "Dependent", "Declined from admission"] },
          { name: "Discharge Instructions Provided", type: "select", required: true,
            options: ["Yes - verbal and written HEP provided", "Yes - verbal instructions only", "Yes - written HEP only", 
                      "No - patient declined", "No - patient unable to receive", "N/A - patient deceased"] },
          { name: "Home Exercise Program Details", type: "textarea",
            placeholder: "List exercises included in discharge HEP with frequency and precautions" },
          { name: "Patient/Caregiver Verbalized Understanding", type: "select", required: true,
            options: ["Yes - demonstrated full understanding", "Yes - with some clarification needed", "Partial understanding - reinforced teaching", 
                      "Limited understanding - referred to physician", "Unable to assess", "N/A - patient deceased"] },
          { name: "Equipment at Discharge", type: "textarea",
            placeholder: "List DME/assistive devices patient has at discharge (walker, wheelchair, orthotics, etc.)" },
          { name: "Recommendations", type: "textarea", required: true,
            placeholder: "Recommendations for continued care:\n- Continue HEP independently\n- Outpatient PT referral\n- Aquatic therapy\n- Community exercise program" },
          { name: "Follow-up Appointments", type: "textarea", placeholder: "Scheduled follow-up appointments or referrals" },
          { name: "PT Discharge Summary", type: "textarea", required: true,
            placeholder: "Comprehensive narrative summary of the patient's PT episode: reason for referral, interventions provided, patient response, and current functional status." },
          { name: "Physician Notified of Discharge", type: "select", required: true,
            options: ["Yes - verbal notification", "Yes - written/fax notification", "Yes - via EMR/portal", "Pending notification", "N/A - physician ordered discharge"] },
          { name: "Physician Notification Date", type: "date" },
          { name: "Receiving Agency Name", type: "text",
            placeholder: "Name of agency/facility patient is transferring to (if applicable)" },
          { name: "Receiving Agency Contact", type: "text",
            placeholder: "Phone number or contact person at receiving agency" },
          { name: "Continuity of Care Notes", type: "textarea",
            placeholder: "Critical information for receiving provider: current care needs, equipment requirements, caregiver training status, pending issues" },
          { name: "Services Provided Summary", type: "textarea",
            placeholder: "Total visits/hours by discipline during episode" }
        ]
      });
    }

    // ========== DIAGNOSIS AND CODING (all types) ==========
    categories.push({
      name: "Diagnosis and Coding",
      priority: 1,
      required: true,
      fields: [
        { name: "Primary ICD-10 Code", type: "select", required: true },
        { name: "Secondary ICD-10 Codes", type: "custom-icd10-list" as any },
        { name: "Medical Necessity - Clinical Complexity", type: "textarea", required: true,
          showFor: ['initial', 'recertification'] as AssessmentType[],
          placeholder: "Describe the patient's medical condition and complexity. Include:\n• Primary diagnosis and relevant comorbidities\n• Medical history affecting therapy needs\n• Why this condition requires skilled therapy (not routine/maintenance)\n• Technology dependence (ventilator, trach, feeding tube, etc.)" },
        { name: "Medical Necessity - Skilled Care Requirements", type: "textarea", required: true,
          showFor: ['initial', 'recertification'] as AssessmentType[],
          placeholder: "Explain WHY skilled therapy is required:\n• Specific skilled techniques needed (not routine exercises)\n• Clinical judgment required for assessment/modification\n• Safety monitoring needed during treatment\n• Complex presentation requiring therapist expertise\n• Why caregiver or aide cannot provide this service" },
        { name: "Medical Necessity - Functional Impact", type: "textarea", required: true,
          showFor: ['initial', 'recertification'] as AssessmentType[],
          placeholder: "Describe how impairments affect patient's function:\n• ADL limitations\n• Mobility/communication limitations\n• Safety risks without therapy\n• Impact on caregiver burden" },
        { name: "Medical Necessity - Expected Outcomes", type: "textarea", required: true,
          showFor: ['initial', 'recertification'] as AssessmentType[],
          placeholder: "What functional improvements are expected with therapy?\n• Specific measurable outcomes\n• Timeframe for anticipated progress\n• How therapy will reduce hospitalizations/complications" }
      ]
    });

    // ========== FUNCTIONAL LIMITATIONS (CMS-485 Section 8) ==========
    if (assessmentType !== 'discharge') {
      categories.push({
        name: "Functional Limitations",
        priority: 1.5,
        required: true,
        showFor: ['initial', 'recertification'] as AssessmentType[],
        description: "CMS-485 Section 8: Check all functional limitations that apply (at least one required)",
        fields: [
          { name: "Functional Limitations Checkboxes", type: "checkbox-group", required: true,
            options: [
              "Ambulation",
              "Amputation",
              "Bowel/Bladder (incontinence)",
              "Contracture",
              "Hearing",
              "Paralysis",
              "Endurance",
              "Speech",
              "Legally Blind",
              "Dyspnea with minimal exertion"
            ]
          },
          { name: "Other Functional Limitations", type: "text",
            placeholder: "Specify other limitations (e.g., ventilator dependent, non-ambulatory)" }
        ]
      });
    }

    // ========== HOME HEALTH CERTIFICATION (home health, initial and recert only) ==========
    if (formConfig.settingType === 'home-health' && assessmentType !== 'discharge') {
      categories.push({
        name: "Home Health Certification",
        priority: 2,
        required: true,
        description: "CMS-485 Section 15: Homebound status and service orders",
        fields: [
          { name: "Homebound Status Criteria (Check all that apply)", type: "checkbox-group", required: true,
            options: [
              "Patient leaves home infrequently and with considerable and taxing effort",
              "Patient requires supportive devices",
              "Patient requires special transportation",
              "Patient requires assistance of another person",
              "Normal inability to leave home due to medical condition",
              "Physician has determined leaving home is medically contraindicated"
            ]
          },
          { name: "Supportive Devices Required", type: "text",
            placeholder: "Specify devices: walker, wheelchair, oxygen, ventilator, etc." },
          { name: "Special Transportation Details", type: "text",
            placeholder: "Describe transport needs: medical van, stretcher transport, ventilator-equipped vehicle" },
          { name: "Assistance Required", type: "textarea",
            placeholder: "Describe assistance needed: supervision, physical assist, continuous skilled nursing, cannot be left unattended" },
          { name: "Homebound Status Justification", type: "textarea", required: true,
            placeholder: "Provide detailed justification for homebound status based on the criteria checked above. Explain why patient cannot safely leave home without considerable effort or assistance." },
          { name: "Referring Physician", type: "text", required: assessmentType === 'initial',
            showFor: ['initial'] as AssessmentType[],
            placeholder: "Physician name who referred/ordered PT services" },
          { name: "Referring Physician NPI", type: "text",
            showFor: ['initial'] as AssessmentType[],
            placeholder: "10-digit NPI number" },
          { name: "Start of Care Date", type: "date", required: true, showFor: ['initial'] as AssessmentType[] }
        ]
      });
    }

    // ========== PHYSICIAN ORDERS AND CERTIFICATION (initial and recert only) ==========
    if (assessmentType !== 'discharge') {
      categories.push({
        name: "Physician Orders and Certification",
        priority: 2.5,
        required: true,
        showFor: ['initial', 'recertification'] as AssessmentType[],
        description: "Track physician order and signature status for CMS-485 compliance (Sections 14 & 17)",
        fields: [
          { name: "Verbal Order Received", type: "select", required: true,
            options: ["Yes", "No"] },
          { name: "Verbal Order Date", type: "date",
            placeholder: "Date verbal order was received" },
          { name: "Verbal Order Received By", type: "text",
            placeholder: "Name of clinician who received verbal order (e.g., Jennifer Martinez, RN)" },
          { name: "Written POC Sent to Physician Date", type: "date", required: true,
            placeholder: "Date compiled POC was sent to physician for signature" },
          { name: "Method POC Sent to Physician", type: "select", required: true,
            options: ["Fax", "Secure Email", "Portal", "In-person", "Mail"] },
          { name: "Physician Received/Reviewed POC Date", type: "date",
            placeholder: "Date physician confirmed receipt and review (must be within 14 days of verbal order)" },
          { name: "Physician Signature Status", type: "select", required: true,
            options: [
              "Pending signature",
              "Signed - paper",
              "Signed - electronic",
              "Signed - fax",
              "Physician declined to sign",
              "Unable to obtain signature"
            ]
          },
          { name: "Date Physician Signed POC", type: "date",
            placeholder: "Date physician signed the plan of care" },
          ...(medicareEnabled ? [
          { name: "Face-to-Face Encounter Required", type: "select",
            options: ["Yes - Medicare patient", "No - Medicaid/other payor"] },
          { name: "Face-to-Face Encounter Date", type: "date",
            placeholder: "Must be within 90 days before or 30 days after SOC" },
          { name: "Face-to-Face Encounter Type", type: "select",
            options: ["In-person visit", "Telehealth (COVID-19 waiver)", "Hospital discharge visit"] },
          { name: "Face-to-Face Clinician Name", type: "text",
            placeholder: "Physician or NPP who performed face-to-face encounter" }
          ] : [])
        ]
      });
    }

    // ========== PATIENT HISTORY (all types but abbreviated for recert/discharge) ==========
    categories.push({
      name: "Patient History",
      priority: 3,
      required: true,
      description: assessmentType === 'recertification' ? "Update patient history. Focus on changes since last evaluation." : undefined,
      fields: [
        { name: "Chief Complaint", type: "textarea", required: assessmentType !== 'discharge',
          placeholder: "Primary reason for PT referral" },
        { name: "History of Present Illness", type: "textarea", required: assessmentType === 'initial',
          showFor: ['initial', 'recertification'] as AssessmentType[],
          placeholder: "Onset, duration, mechanism of injury, previous treatment" },
        { name: "Past Medical History", type: "textarea", showFor: ['initial'] as AssessmentType[],
          placeholder: "Relevant medical history affecting PT treatment" },
        { name: "Surgical History", type: "textarea", showFor: ['initial'] as AssessmentType[],
          placeholder: "Relevant surgeries with approximate dates" },
        { name: "Prior Level of Function", type: "textarea", required: assessmentType === 'initial',
          showFor: ['initial', 'recertification'] as AssessmentType[],
          placeholder: "Functional status before current condition/injury" },
        { name: "Current Medications", type: "textarea", 
          placeholder: "Medications affecting PT (pain meds, muscle relaxants, blood thinners, etc.)" },
        { name: "Precautions/Contraindications", type: "textarea", required: true,
          placeholder: "Weight bearing status, movement restrictions, cardiac precautions, fall risk, etc." }
      ]
    });

    // ========== MENTAL STATUS (CMS-485 Section 10) ==========
    if (assessmentType !== 'discharge') {
      categories.push({
        name: "Mental Status",
        priority: 3.8,
        required: true,
        showFor: ['initial', 'recertification'] as AssessmentType[],
        description: "CMS-485 Section 10: Patient's mental and cognitive status",
        fields: [
          { name: "Mental Status Indicators", type: "checkbox-group", required: true,
            options: [
              "Oriented",
              "Disoriented",
              "Forgetful",
              "Depressed",
              "Agitated",
              "Comatose"
            ]
          },
          { name: "Orientation Level", type: "select",
            options: [
              "Oriented x4 (person, place, time, situation)",
              "Oriented x3 (person, place, time)",
              "Oriented x2",
              "Oriented x1",
              "Disoriented",
              "N/A - pediatric/unable to assess"
            ]
          },
          { name: "Mental Status Description", type: "textarea", required: true,
            placeholder: formConfig.patientType === 'pediatric'
              ? "For pediatric: Describe cognitive/developmental level, responsiveness to caregivers, ability to follow commands, communication method"
              : "Describe cognitive function, memory, attention, safety awareness, judgment as it relates to therapy" },
          { name: "Cognitive Level (Pediatric)", type: "text",
            showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "e.g., Functions at 18-24 month level" }
        ]
      });
    }

    // ========== PEDIATRIC INFORMATION (pediatric patients) ==========
    if (formConfig.patientType === 'pediatric') {
      categories.push({
        name: "Pediatric Information",
        priority: 3.2,
        required: true,
        description: assessmentType === 'recertification' ? "Update pediatric information. Birth history fields are optional." : undefined,
        fields: [
          { name: "Chronological Age", type: "text", required: true, placeholder: "e.g., 2 years 4 months" },
          { name: "Adjusted/Corrected Age", type: "text", placeholder: "If premature, corrected age for developmental assessment" },
          { name: "Gestational Age at Birth", type: "text", showFor: ['initial'] as AssessmentType[], placeholder: "e.g., 32 weeks" },
          { name: "Birth Weight", type: "text", showFor: ['initial'] as AssessmentType[], placeholder: "e.g., 1250 grams" },
          { name: "Birth History", type: "textarea", showFor: ['initial'] as AssessmentType[],
            placeholder: "Include NICU stay, birth complications, early interventions" },
          { name: "Primary Caregiver", type: "text", required: assessmentType !== 'discharge', placeholder: "Name and relationship to patient" },
          { name: "Current School/Daycare", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Not applicable (infant/toddler)", "Daycare", "Preschool", "Elementary school", "Homebound", "Early intervention program", "Special education"] },
          { name: "IEP/IFSP Status", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["No IEP/IFSP", "IFSP in place (0-3 years)", "IEP in place (3+ years)", "Evaluation pending", "Not eligible"] },
          { name: "Other Therapies Receiving", type: "textarea", placeholder: "OT, Speech, ABA, etc. with frequency" }
        ]
      });
    }

    // ========== PEDIATRIC MOTOR DEVELOPMENT (pediatric patients, all types) ==========
    if (formConfig.patientType === 'pediatric') {
      categories.push({
        name: "Pediatric Motor Development",
        priority: 3.5,
        required: true,
        description: assessmentType === 'discharge' ? "Final developmental status at discharge." : "Comprehensive motor development assessment.",
        fields: [
          { name: "Gross Motor Development Level", type: "select", required: true,
            options: ["Age appropriate", "Mild delay (1-3 months behind)", "Moderate delay (3-6 months behind)", "Significant delay (6-12 months behind)", "Severe delay (>12 months behind)", "Unable to assess"] },
          { name: "GMFCS Level", type: "select",
            options: ["N/A - not applicable", "Level I - walks without limitations", "Level II - walks with limitations", "Level III - walks with assistive device", "Level IV - limited self-mobility, may use power mobility", "Level V - transported in manual wheelchair"] },
          { name: "Head Control", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["N/A - age appropriate full control", "Full head control in all positions", "Head control in supported sitting", "Emerging head control", "Minimal head control", "No head control"] },
          { name: "Rolling", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["N/A - beyond this milestone", "Rolls both directions independently", "Rolls supine to prone only", "Rolls prone to supine only", "Rolls with assistance", "Unable to roll", "Not yet expected"] },
          { name: "Sitting", type: "select", required: true,
            options: ["Independent floor sitting >5 min", "Independent sitting <5 min", "Sits with hand support", "Sits with external support", "Propped sitting only", "Unable to sit", "Not yet expected"] },
          { name: "Crawling/Creeping", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Reciprocal crawling", "Commando/belly crawling", "Bottom scooting", "Rolls to move", "Not mobile on floor", "Not yet expected", "Skipped - went to walking"] },
          { name: "Pull to Stand", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Pulls to stand independently", "Pulls to stand with minimal assist", "Pulls to stand with moderate assist", "Unable to pull to stand", "Not yet expected"] },
          { name: "Standing Balance", type: "select", required: true,
            options: ["Independent standing >30 sec", "Independent standing <30 sec", "Stands with one hand held", "Stands with two hands held", "Stands at support surface", "Unable to stand", "Not yet expected"] },
          { name: "Cruising", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Cruises independently along furniture", "Cruises with one hand held", "Cruises with two hands held", "Not cruising", "Not yet expected", "N/A - walking"] },
          { name: "Walking", type: "select", required: true,
            options: ["Independent walking - age appropriate gait", "Independent walking - immature gait pattern", "Walks with assistive device", "Walks with one hand held", "Walks with two hands held", "Taking steps with support", "Not yet walking", "Unable to walk"] },
          { name: "Running/Advanced Mobility", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Age appropriate running/jumping", "Emerging running", "Unable to run", "Not yet expected", "N/A - not ambulatory"] },
          { name: "Stairs", type: "select",
            options: ["Reciprocal stair climbing", "Step-to pattern with rail", "Step-to pattern with hand held", "Crawls up stairs", "Unable to do stairs", "Not yet expected"] },
          { name: "Motor Planning/Praxis", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Age appropriate motor planning", "Mild motor planning difficulties", "Moderate motor planning difficulties", "Significant motor planning difficulties", "Unable to assess"] },
          { name: "Muscle Tone", type: "select", required: true,
            options: ["Normal tone throughout", "Hypotonia - generalized", "Hypotonia - axial only", "Hypertonia - mild", "Hypertonia - moderate", "Hypertonia - severe", "Mixed tone", "Fluctuating tone"] },
          { name: "Tone Distribution", type: "textarea", showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "Describe distribution of abnormal tone (e.g., increased tone bilateral LEs, low tone in trunk)" },
          { name: "Primitive Reflexes", type: "textarea", showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "Note any retained primitive reflexes (ATNR, STNR, Moro, etc.) with impact on function" },
          { name: "Postural Reactions", type: "textarea", showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "Righting, equilibrium, and protective reactions present/absent/emerging" },
          { name: "Developmental Concerns Summary", type: "textarea", required: true,
            placeholder: "Summary of primary developmental/motor concerns and functional impact" }
        ]
      });
    }

    // ========== PEDIATRIC PDN PHYSICAL THERAPY (pediatric + home health) ==========
    if (formConfig.patientType === 'pediatric' && formConfig.settingType === 'home-health') {
      categories.push({
        name: "Pediatric PDN Physical Therapy",
        priority: 3.7,
        required: true,
        description: assessmentType === 'discharge' 
          ? "Final status for PDN PT considerations."
          : "Texas Medicaid PDN-specific PT documentation.",
        fields: [
          // Medical Complexity
          { name: "Tracheostomy Status", type: "select", required: true,
            options: assessmentType === 'discharge'
              ? ["No tracheostomy", "Tracheostomy present at discharge", "Tracheostomy decannulated during episode"]
              : ["No tracheostomy", "Tracheostomy present", "Recently decannulated"] },
          { name: "Ventilator Status", type: "select", required: true,
            options: assessmentType === 'discharge'
              ? ["No ventilator", "Ventilator dependent at discharge", "Weaned from ventilator during episode"]
              : ["No ventilator", "Ventilator dependent - continuous", "Ventilator dependent - nocturnal only", "Weaning in progress"] },
          { name: "Medical Stability for PT", type: "select", required: assessmentType !== 'discharge',
            showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Medically stable - proceed with full PT", "Stable with precautions - modified PT", "Unstable - PT on hold", "Unstable - bedside PT only"] },
          { name: "PT Precautions Related to Medical Status", type: "textarea",
            showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "Oxygen requirements during activity, HR/RR limits, desaturation precautions, positioning restrictions" },
          
          // Mobility Equipment
          { name: "Current Mobility Equipment", type: "textarea",
            placeholder: "List all mobility equipment: wheelchair, stander, gait trainer, walker, orthotics, etc." },
          { name: "Equipment Fit and Condition", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["All equipment fits and functions properly", "Equipment needs adjustment", "Equipment needs replacement", "New equipment needed - see recommendations", "No equipment currently"] },
          { name: "Stander Use", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["N/A - ambulatory", "Stander in home - used regularly", "Stander in home - not used regularly", "Stander recommended - not yet obtained", "Stander not indicated"] },
          { name: "Stander Tolerance", type: "text", showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "e.g., Tolerates 30 min 2x/day in supine stander" },
          { name: "Gait Trainer/Walker", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["N/A", "Uses gait trainer - active stepping", "Uses gait trainer - supported only", "Uses posterior walker", "Uses anterior walker", "Recommended - not yet obtained"] },
          
          // Orthotics and Positioning
          { name: "Orthotic Use", type: "select",
            options: ["No orthotics", "AFOs bilateral", "AFO unilateral (R/L)", "SMOs bilateral", "Knee immobilizers", "TLSO/spinal orthotic", "Other - see notes"] },
          { name: "Orthotic Fit and Wear Schedule", type: "textarea", showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "Describe fit, wear schedule, and any skin issues" },
          { name: "Positioning Equipment", type: "textarea", showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "Describe positioning equipment used (wedges, rolls, seating systems) and schedule" },
          { name: "Hip Surveillance Status", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["N/A - no hip concerns", "Hips stable - monitoring", "Hip dysplasia - conservative management", "Post hip surgery - see precautions", "Hip subluxation/dislocation risk"] },
          
          // Caregiver Training
          { name: "Caregiver PT Skill Level", type: "select", required: assessmentType !== 'discharge',
            showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Excellent - independent with HEP and handling", "Good - needs occasional cueing", "Fair - needs regular supervision/training", "Poor - significant training needed", "Unable to participate in PT activities"] },
          { name: "Caregiver Training Provided", type: "textarea",
            placeholder: "Training provided: therapeutic positioning, stretching program, equipment use, transfers, etc." },
          { name: "Caregiver Return Demonstration", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Successful demonstration of all skills", "Partial demonstration - needs practice", "Unable to demonstrate - reteach needed", "Not applicable"] },
          
          // PT in PDN Context
          { name: "PT Visit Frequency Requested", type: "text", required: assessmentType !== 'discharge',
            showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "e.g., 2x/week for 60 min sessions" },
          { name: "PT Frequency Justification", type: "textarea", required: assessmentType !== 'discharge',
            showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "Justify PT frequency based on: developmental needs, medical complexity, caregiver training needs, equipment needs" },
          { name: "Coordination with PDN Staff", type: "textarea", showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "PT activities to be carried over by PDN nurses between PT visits (positioning, stretching, standing program)" }
        ]
      });
    }

    // ========== PAIN ASSESSMENT (all types) ==========
    categories.push({
      name: "Pain Assessment",
      priority: 4,
      required: true,
      fields: [
        { name: "Pain Present", type: "select", required: true,
          options: ["No pain", "Yes - see details below"] },
        { name: "Pain Scale Used", type: "select",
          options: ["Numeric Pain Scale (0-10)", "Wong-Baker FACES", "FLACC (pediatric/non-verbal)", "PAINAD (dementia)", "Visual Analog Scale"] },
        { name: "Pain Level at Rest", type: "select",
          options: ["0 - No pain", "1-3 Mild", "4-6 Moderate", "7-10 Severe", "Unable to assess"] },
        { name: "Pain Level with Activity", type: "select",
          options: ["0 - No pain", "1-3 Mild", "4-6 Moderate", "7-10 Severe", "Unable to assess"] },
        { name: "Pain Location", type: "textarea", placeholder: "Describe location(s) of pain" },
        { name: "Pain Character", type: "select",
          options: ["Aching", "Sharp/stabbing", "Burning", "Throbbing", "Cramping", "Numbness/tingling", "Radiating", "Multiple types"] },
        { name: "Pain Aggravating Factors", type: "textarea", placeholder: "Activities or positions that increase pain" },
        { name: "Pain Relieving Factors", type: "textarea", placeholder: "Activities, positions, or treatments that decrease pain" }
      ]
    });

    // ========== RANGE OF MOTION (initial and recert, abbreviated for peds) ==========
    if (assessmentType !== 'discharge') {
      categories.push({
        name: "Range of Motion",
        priority: 5,
        required: false,
        description: formConfig.patientType === 'pediatric' ? "Document significant ROM limitations affecting function." : undefined,
        fields: [
          { name: "ROM Assessment Method", type: "select",
            options: ["Goniometric measurement", "Visual estimation", "Functional ROM assessment", "Not assessed - see notes"] },
          { name: "Cervical ROM", type: "textarea", placeholder: "Flexion/Extension/Rotation/Lateral flexion - note limitations" },
          { name: "Shoulder ROM", type: "textarea", placeholder: "Flexion/Abduction/ER/IR - bilateral comparison" },
          { name: "Elbow/Wrist ROM", type: "textarea", placeholder: "Note any limitations" },
          { name: "Hip ROM", type: "textarea", required: formConfig.patientType === 'pediatric',
            placeholder: formConfig.patientType === 'pediatric' 
              ? "Hip flexion, extension, abduction - CRITICAL for CP/developmental dx"
              : "Flexion/Extension/Abduction/Adduction/IR/ER" },
          { name: "Knee ROM", type: "textarea", placeholder: "Flexion/Extension - note any flexion contractures" },
          { name: "Ankle ROM", type: "textarea", required: formConfig.patientType === 'pediatric',
            placeholder: formConfig.patientType === 'pediatric'
              ? "Dorsiflexion with knee extended and flexed - note any tightness"
              : "Dorsiflexion/Plantarflexion - note any limitations" },
          { name: "ROM Limitations Summary", type: "textarea",
            placeholder: "Summary of significant ROM limitations and functional impact" }
        ]
      });
    }

    // ========== STRENGTH ASSESSMENT (initial and recert) ==========
    if (assessmentType !== 'discharge') {
      categories.push({
        name: "Strength Assessment",
        priority: 6,
        required: false,
        description: formConfig.patientType === 'pediatric' ? "Use age-appropriate strength assessment methods." : undefined,
        fields: [
          { name: "Strength Assessment Method", type: "select",
            options: formConfig.patientType === 'pediatric'
              ? ["Manual muscle testing (modified)", "Functional strength assessment", "Observation of antigravity movement", "Unable to assess formally"]
              : ["Manual Muscle Testing (MMT)", "Functional strength assessment", "Dynamometer", "Not assessed"] },
          { name: "Upper Extremity Strength", type: "textarea",
            placeholder: formConfig.patientType === 'pediatric'
              ? "Describe UE strength functionally (reaching, weight bearing on hands, etc.)"
              : "Shoulder/Elbow/Wrist - bilateral comparison with MMT grades" },
          { name: "Core/Trunk Strength", type: "select", required: true,
            options: formConfig.patientType === 'pediatric'
              ? ["Age appropriate core strength", "Mild core weakness", "Moderate core weakness - affects sitting/transitions", "Significant core weakness - requires support", "Unable to assess"]
              : ["5/5 - Normal", "4/5 - Good", "3/5 - Fair", "2/5 - Poor", "1/5 - Trace", "0/5 - None"] },
          { name: "Hip Strength", type: "textarea",
            placeholder: "Hip flexors/extensors/abductors/adductors" },
          { name: "Knee Strength", type: "textarea",
            placeholder: "Quadriceps/Hamstrings" },
          { name: "Ankle Strength", type: "textarea",
            placeholder: "Dorsiflexors/Plantarflexors" },
          { name: "Strength Summary", type: "textarea",
            placeholder: "Summary of significant weakness and functional impact" }
        ]
      });
    }

    // ========== BALANCE & COORDINATION (all types) ==========
    categories.push({
      name: "Balance & Coordination",
      priority: 7,
      required: true,
      fields: [
        { name: "Static Sitting Balance", type: "select", required: true,
          options: ["Normal/Independent", "Good - minimal sway", "Fair - moderate sway, recovers", "Poor - needs support", "Unable to sit unsupported"] },
        { name: "Dynamic Sitting Balance", type: "select", required: true,
          options: ["Normal - reaches all directions", "Good - reaches with minimal loss of balance", "Fair - limited reaching", "Poor - loses balance easily", "Unable to assess"] },
        { name: "Static Standing Balance", type: "select", required: true,
          options: ["Normal - stands >30 sec unsupported", "Good - stands with minimal sway", "Fair - stands <30 sec or with sway", "Poor - needs support", "Unable to stand"] },
        { name: "Dynamic Standing Balance", type: "select", required: true,
          options: ["Normal - weight shift/reaching intact", "Good - mild difficulty with weight shift", "Fair - moderate difficulty, needs verbal cues", "Poor - significant difficulty, needs contact guard", "Unable to assess"] },
        { name: "Balance Assessment Tool", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
          options: formConfig.patientType === 'pediatric'
            ? ["PBS (Pediatric Balance Scale)", "Clinical observation", "Not formally assessed", "N/A - developmental level"]
            : ["Berg Balance Scale", "Tinetti", "TUG (Timed Up and Go)", "4-Stage Balance Test", "Clinical observation", "Not formally assessed"] },
        { name: "Balance Score", type: "text", showFor: ['initial', 'recertification'] as AssessmentType[],
          placeholder: "Score and interpretation (e.g., Berg: 42/56 - moderate fall risk)" },
        { name: "Coordination", type: "select",
          options: ["Normal coordination", "Mild incoordination", "Moderate incoordination - affects function", "Severe incoordination", "Unable to assess"] },
        { name: "Balance/Coordination Summary", type: "textarea",
          placeholder: "Summary of balance deficits and safety implications" }
      ]
    });

    // ========== GAIT ANALYSIS (initial and recert for ambulatory patients) ==========
    if (assessmentType !== 'discharge') {
      categories.push({
        name: "Gait Analysis",
        priority: 8,
        required: false,
        fields: [
          { name: "Ambulation Status", type: "select", required: true,
            options: ["Independent - no device", "Independent with device", "Supervision required", "Contact guard", "Minimal assist", "Moderate assist", "Maximum assist", "Non-ambulatory"] },
          { name: "Assistive Device", type: "select",
            options: ["None", "Straight cane", "Quad cane", "Hemi-walker", "Rolling walker", "Front-wheeled walker", "Posterior walker (peds)", "Gait trainer (peds)", "Crutches", "Wheelchair only"] },
          { name: "Gait Pattern", type: "select",
            options: formConfig.patientType === 'pediatric'
              ? ["Age-appropriate gait", "Immature gait pattern", "Toe walking", "Crouch gait", "Scissoring gait", "Ataxic gait", "Hemiplegic gait", "Unable to assess - not walking"]
              : ["Normal gait pattern", "Antalgic gait", "Trendelenburg gait", "Steppage gait", "Shuffling gait", "Festinating gait", "Ataxic gait", "Scissoring gait"] },
          { name: "Gait Deviations", type: "textarea",
            placeholder: "Describe specific gait deviations observed" },
          { name: "Gait Speed", type: "text", showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "e.g., 0.8 m/s (10-meter walk test)" },
          { name: "Distance Ambulated", type: "text",
            placeholder: "e.g., 150 feet with rolling walker, min A" },
          { name: "Endurance", type: "select",
            options: ["Good - no fatigue with ambulation", "Fair - fatigues after moderate distance", "Poor - fatigues quickly", "Very poor - limited to few steps"] },
          { name: "Gait Safety", type: "select", required: true,
            options: ["Safe - independent", "Safe with device/supervision", "Questionable safety - fall risk", "Unsafe - requires assist", "Non-ambulatory"] }
        ]
      });
    }

    // ========== FUNCTIONAL MOBILITY (all types) ==========
    categories.push({
      name: "Functional Mobility",
      priority: 9,
      required: true,
      description: assessmentType === 'discharge' ? "Compare to baseline/admission status." : undefined,
      fields: [
        { name: "Bed Mobility", type: "select", required: true,
          options: ["Independent", "Modified independent", "Supervision", "Minimal assist", "Moderate assist", "Maximum assist", "Dependent"] },
        { name: "Supine to Sit", type: "select", required: true,
          options: ["Independent", "Modified independent", "Supervision", "Minimal assist", "Moderate assist", "Maximum assist", "Dependent"] },
        { name: "Sit to Stand", type: "select", required: true,
          options: ["Independent", "Modified independent/uses arms", "Supervision", "Minimal assist", "Moderate assist", "Maximum assist", "Dependent", "Unable"] },
        { name: "Transfers", type: "select", required: true,
          options: ["Independent all surfaces", "Modified independent", "Supervision", "Minimal assist", "Moderate assist", "Maximum assist", "Dependent", "Mechanical lift required"] },
        { name: "Floor Transfers", type: "select",
          options: formConfig.patientType === 'pediatric'
            ? ["Independent floor mobility", "Needs assist to/from floor", "Unable to get up from floor", "N/A - not ambulatory"]
            : ["Independent up/down from floor", "Needs assist", "Unable to get up from floor", "Not assessed"] },
        { name: "Wheelchair Mobility", type: "select",
          options: ["N/A - ambulatory", "Independent manual WC", "Independent power WC", "Needs assist for propulsion", "Dependent for mobility"] },
        { name: "Stairs", type: "select",
          options: ["Independent - reciprocal", "Independent - step-to pattern", "Needs rail", "Minimal assist", "Moderate assist", "Maximum assist", "Unable/not assessed", "N/A"] },
        { name: "Functional Mobility Summary", type: "textarea", required: true,
          placeholder: "Summary of mobility status and assistance levels" }
      ]
    });

    // ========== EQUIPMENT & ASSISTIVE DEVICES (initial and recert) ==========
    if (assessmentType !== 'discharge') {
      categories.push({
        name: "Equipment & Assistive Devices",
        priority: 10,
        required: false,
        fields: [
          { name: "Current Equipment", type: "textarea",
            placeholder: "List all current DME and assistive devices (wheelchair, walker, orthotics, etc.)" },
          { name: "Equipment Condition", type: "select",
            options: ["All equipment in good condition", "Equipment needs adjustment/repair", "Equipment needs replacement", "No equipment currently"] },
          { name: "Equipment Recommendations", type: "textarea",
            placeholder: "New equipment needed, equipment modifications, or equipment that can be discontinued" },
          { name: "Orthotics/Prosthetics", type: "textarea",
            placeholder: "Current orthotics/prosthetics, fit, wear schedule, issues" },
          { name: "Equipment Training Needed", type: "textarea",
            placeholder: "Training needed for patient/caregiver on equipment use" }
        ]
      });
    }

    // ========== HOME SAFETY ASSESSMENT (home health, initial and recert) ==========
    if (formConfig.settingType === 'home-health' && assessmentType !== 'discharge') {
      categories.push({
        name: "Home Safety Assessment",
        priority: 11,
        required: assessmentType === 'initial',
        description: assessmentType === 'recertification' ? "Document any changes to home environment." : undefined,
        fields: [
          { name: "Overall Home Safety", type: "select", required: assessmentType === 'initial',
            options: ["Safe for current mobility level", "Minor hazards identified - addressed", "Minor hazards identified - pending", "Significant hazards - intervention needed"] },
          { name: "Entrance/Exit Access", type: "select",
            options: ["Safe access - no steps/barriers", "Steps with rail - patient can navigate", "Steps without rail - needs modification", "Ramp present", "Unsafe access - barrier to care"] },
          { name: "Floor Surfaces", type: "select",
            options: ["Safe surfaces throughout", "Throw rugs present - fall risk", "Uneven surfaces", "Clutter present - cleared", "Clutter present - remains hazard"] },
          { name: "Lighting", type: "select",
            options: ["Adequate lighting throughout", "Poor lighting - addressed", "Poor lighting - remains concern"] },
          { name: "Bathroom Safety", type: "select",
            options: ["Grab bars present and adequate", "Grab bars needed", "Tub/shower accessible", "Tub bench/shower chair present", "Raised toilet seat present/needed"] },
          { name: "Bedroom Safety", type: "select",
            options: ["Safe setup", "Bed height appropriate", "Bed height needs adjustment", "Hospital bed present", "Clear path to bathroom"] },
          { name: "Home Modifications Recommended", type: "textarea",
            placeholder: "Recommended modifications (grab bars, ramp, stair rail, etc.)" },
          { name: "Home Safety Notes", type: "textarea",
            placeholder: "Additional safety concerns or recommendations" }
        ]
      });
    }

    // ========== ACTIVITIES PERMITTED (PT only, CMS-485 Section 9) ==========
    if (assessmentType !== 'discharge') {
      categories.push({
        name: "Activities Permitted",
        priority: 3.9,
        required: true,
        description: "CMS-485 Section 9: Activity level restrictions and precautions",
        fields: [
          { name: "Activity Level Restrictions", type: "checkbox-group",
            options: [
              "Complete bedrest",
              "Bed rest with bathroom privileges (BRP)",
              "Up as tolerated",
              "Independent at home",
              "Exercises per PT/OT"
            ]
          },
          { name: "Weight-Bearing Status", type: "select", required: true,
            options: [
              "Weight bearing as tolerated (WBAT)",
              "Partial weight bearing (PWB) - specify percentage",
              "Touch-down weight bearing (TDWB)",
              "Non-weight bearing (NWB)",
              "Full weight bearing (FWB)",
              "No restrictions"
            ]
          },
          { name: "Weight-Bearing Percentage", type: "text",
            placeholder: "If PWB, specify percentage (e.g., 50%)" },
          { name: "Assistive Devices Required", type: "checkbox-group",
            options: ["Walker", "Cane", "Crutches", "Wheelchair", "None"] },
          { name: "Special Precautions", type: "checkbox-group",
            options: [
              "Seizure precautions",
              "Fall precautions",
              "Aspiration precautions",
              "Hip precautions (post-surgical)",
              "Cardiac precautions",
              "Oxygen required during activity",
              "Ventilator precautions"
            ]
          },
          { name: "Activity Notes", type: "textarea",
            placeholder: "Additional activity restrictions, positioning requirements, or special considerations" }
        ]
      });
    }

    // ========== TREATMENT PLAN (initial and recert) ==========
    if (assessmentType !== 'discharge') {
      categories.push({
        name: "Treatment Plan",
        priority: 12,
        required: true,
        fields: [
          { name: "Plan Created By", type: "text", required: true, readonly: true,
            placeholder: "Auto-populated from current user (e.g., Sarah Johnson, PT, DPT)" },
          { name: "Plan Creation Date", type: "date", required: true, readonly: true,
            placeholder: "Auto-populated with today's date" },
          { name: "Service Frequency - Visits Per Week", type: "number", required: true,
            placeholder: "e.g., 2",
            min: 1,
            max: 7 },
          { name: "Service Frequency - Minutes Per Visit", type: "select", required: true,
            options: ["30", "45", "60", "75", "90", "120"] },
          { name: "Service Frequency - Total Hours Per Week", type: "text", readonly: true,
            placeholder: "Auto-calculated from visits and minutes" },
          { name: "Service Duration - Start Date", type: "date", required: true },
          { name: "Service Duration - Anticipated End Date", type: "date", required: true },
          { name: "Service Duration - Total Days", type: "text", readonly: true,
            placeholder: "Auto-calculated from start and end dates" },
          { name: "Certification Period", type: "select", required: true,
            options: ["60 days", "90 days", "120 days", "180 days"] },
          { name: "Short-Term Goals (60-90 days)", type: "textarea", required: true,
            placeholder: "List short-term goals (target 60-90 days from SOC). For each goal include:\n• Goal statement\n• Target date\n• Baseline measurement\n• Target measurement\n• How progress will be measured" },
          { name: "Long-Term Goals (120-180 days)", type: "textarea", required: true,
            placeholder: "List long-term goals (target 120-180 days from SOC). For each goal include:\n• Goal statement\n• Target date\n• Baseline measurement\n• Target measurement\n• How progress will be measured" },
          { name: "Rehabilitation Potential", type: "select", required: true,
            options: [
              "Excellent - expected to meet all goals",
              "Good - expected to meet most goals",
              "Fair - expected to make functional gains",
              "Guarded - limited expected progress",
              "Poor - maintenance/prevention focus"
            ]
          },
          { name: "Rehabilitation Potential Rationale", type: "textarea", required: true,
            placeholder: "Explain rationale for rehab potential based on patient's condition, cognitive status, caregiver support, compliance, medical stability" },
          { name: "Anticipated Discharge Plan", type: "textarea", required: true,
            showFor: ['initial'] as AssessmentType[],
            placeholder: "Describe anticipated discharge: expected to return to prior level of function, ongoing skilled care anticipated, specific discharge criteria, expected timeframe for discharge" },
          { name: "Planned Interventions", type: "textarea", required: true,
            placeholder: "Therapeutic exercises, gait training, balance training, transfer training, caregiver education, etc." },
          { name: "Home Exercise Program", type: "textarea",
            placeholder: "Exercises to be performed between PT visits with frequency" },
          { name: "Coordination of Care", type: "textarea",
            placeholder: "Coordination with nursing, OT, speech, physician, etc." }
        ]
      });
    }

    // ========== PT ATTESTATION (all types) ==========
    categories.push({
      name: "PT Attestation",
      priority: 13,
      required: true,
      description: assessmentType === 'discharge' ? "Final attestation for discharge evaluation." : undefined,
      fields: [
        { name: "PT Summary", type: "textarea", required: true,
          placeholder: assessmentType === 'discharge'
            ? "Summary of PT episode of care, patient progress, and discharge status."
            : "Summary of key findings, functional limitations, and PT plan." },
        { name: "Skilled PT Justification", type: "textarea", required: assessmentType !== 'discharge',
          showFor: ['initial', 'recertification'] as AssessmentType[],
          placeholder: "Justify why skilled PT services are required (vs. maintenance or caregiver program)" },
        { name: "Rehab Potential", type: "select", required: assessmentType !== 'discharge',
          showFor: ['initial', 'recertification'] as AssessmentType[],
          options: ["Excellent - expected to meet all goals", "Good - expected to meet most goals", "Fair - expected to make functional gains", "Guarded - limited expected progress", "Poor - maintenance/prevention focus"] },
        { name: "PT Signature Attestation", type: "select", required: true,
          options: assessmentType === 'discharge'
            ? ["I attest this discharge evaluation is complete and accurate", "Evaluation pending additional information"]
            : ["I attest this evaluation is complete and accurate", "Evaluation pending additional information"] }
      ]
    });

    return categories.sort((a, b) => a.priority - b.priority);
  };

  // Form data state management
  const [formData, setFormData] = useState(() => {
    if (existingData && typeof existingData === 'object' && Object.keys(existingData).length > 0) {
      // Load secondary ICD-10 codes from existing data
      const existingSecondary = existingData.categories?.['Diagnosis and Coding-Secondary ICD-10 Codes'];
      if (existingSecondary && typeof existingSecondary === 'string') {
        const codes = splitIcd10Field(existingSecondary);
        setSecondaryICD10Codes(codes);
      }
      return existingData;
    }

    const categories = {};
    getAssessmentCategories().forEach(category => {
      category.fields.forEach(field => {
        categories[`${category.name}-${field.name}`] = "";
      });
    });
    
    return {
      id: "",
      date: new Date().toISOString().split('T')[0],
      provider: "Current User, PT",
      status: "Pending",
      category: "PT",
      lastUpdated: new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true }),
      categories: categories,
      formConfig: formConfig
    };
  });
  
  useEffect(() => {
    // Only run this effect once when existingData is first loaded
    // Skip if we've already loaded the initial data to prevent resetting user changes
    if (initialLoadCompleteRef.current) {
      return;
    }

    if (existingData && typeof existingData === 'object' && Object.keys(existingData).length > 0) {
      setFormData(existingData);
      // Merge existing formConfig with defaults to ensure all fields are present
      const mergedConfig = withPediatricFirstConfig({ ...defaultFormConfig, ...existingData.formConfig });
      // Normalize assessment type from backend format to internal format
      mergedConfig.assessmentType = normalizeAssessmentType(mergedConfig.assessmentType);
      setFormConfig(mergedConfig);
      // Mark initial load as complete after loading existing data
      initialLoadCompleteRef.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- defaultFormConfig is a fresh object every render; only a new existingData should reload the form
  }, [existingData]);

  // Auto-calculate total hours per week
  const serviceVisitsPerWeek = formData.categories?.['Treatment Plan-Service Frequency - Visits Per Week'];
  const serviceMinutesPerVisit = formData.categories?.['Treatment Plan-Service Frequency - Minutes Per Visit'];
  useEffect(() => {
    const visitsPerWeek = parseInt(serviceVisitsPerWeek || '0');
    const minutesPerVisit = parseInt(serviceMinutesPerVisit || '0');
    
    if (visitsPerWeek > 0 && minutesPerVisit > 0) {
      const totalHours = (visitsPerWeek * minutesPerVisit) / 60;
      setFormData(prev => ({
        ...prev,
        categories: {
          ...prev.categories,
          'Treatment Plan-Service Frequency - Total Hours Per Week': `${totalHours.toFixed(1)} hours`
        }
      }));
    }
  }, [serviceVisitsPerWeek, serviceMinutesPerVisit]);

  // Auto-calculate duration in days
  const serviceStartDate = formData.categories?.['Treatment Plan-Service Duration - Start Date'];
  const serviceEndDate = formData.categories?.['Treatment Plan-Service Duration - Anticipated End Date'];
  useEffect(() => {
    const startDate = serviceStartDate;
    const endDate = serviceEndDate;
    
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const days = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
      
      setFormData(prev => ({
        ...prev,
        categories: {
          ...prev.categories,
          'Treatment Plan-Service Duration - Total Days': `${days} days`
        }
      }));
    }
  }, [serviceStartDate, serviceEndDate]);

  // Auto-populate plan creator and date on form initialization
  useEffect(() => {
    if (!editingId && !formData.categories?.['Treatment Plan-Plan Created By']) {
      setFormData(prev => ({
        ...prev,
        categories: {
          ...prev.categories,
          'Treatment Plan-Plan Created By': formData.provider,
          'Treatment Plan-Plan Creation Date': new Date().toISOString().split('T')[0]
        }
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- auto-fill only on open/provider change; adding the field would re-fill it after the user clears it
  }, [editingId]);

  useEffect(() => {
    const categories = {};
    getAssessmentCategories().forEach(category => {
      category.fields.forEach(field => {
        const fieldKey = `${category.name}-${field.name}`;
        categories[fieldKey] = formData.categories?.[fieldKey] || "";
      });
    });
    
    setFormData(prev => ({
      ...prev,
      categories: { ...prev.categories, ...categories },
      formConfig: formConfig
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- runs only when the form type fields change; adding formConfig/formData would rebuild the categories on every edit
  }, [formConfig.assessmentType, formConfig.patientType, formConfig.settingType, formConfig.showOptionalSections]);

  const handleInputChange = (fieldKey, value) => {
    setFormData(prev => ({
      ...prev,
      categories: { ...(prev.categories || {}), [fieldKey]: value }
    }));
  };

  const handleAddSecondaryICD10 = () => {
    if (selectedSecondaryICD10 && !secondaryICD10Codes.includes(selectedSecondaryICD10)) {
      const updatedCodes = [...secondaryICD10Codes, selectedSecondaryICD10];
      setSecondaryICD10Codes(updatedCodes);
      setSelectedSecondaryICD10('');
      // Update form data
      setFormData(prev => ({
        ...prev,
        categories: { ...(prev.categories || {}), 'Diagnosis and Coding-Secondary ICD-10 Codes': joinIcd10Field(updatedCodes) }
      }));
    }
  };

  const handleRemoveSecondaryICD10 = (code) => {
    const updatedCodes = secondaryICD10Codes.filter(c => c !== code);
    setSecondaryICD10Codes(updatedCodes);
    // Update form data
    setFormData(prev => ({
      ...prev,
      categories: { ...(prev.categories || {}), 'Diagnosis and Coding-Secondary ICD-10 Codes': joinIcd10Field(updatedCodes) }
    }));
  };

  const handleConfigChange = (key, value) => {
    setFormConfig(prev => ({ ...prev, [key]: value }));
  };

  const toggleSection = (sectionName) => {
    setCollapsedSections(prev => ({ ...prev, [sectionName]: !prev[sectionName] }));
  };

  const toggleAllSections = (collapse: boolean) => {
    const newState: any = {};
    Object.keys(collapsedSections).forEach(key => {
      newState[key] = collapse;
    });
    setCollapsedSections(newState);
    setConfigSectionCollapsed(collapse);
  };

  const getCategoryColorClasses = (categoryName) => {
    const colorMap = {
      "Recertification Summary": { bg: "bg-amber-50", border: "border-amber-200", hover: "hover:bg-amber-100" },
      "Discharge Summary": { bg: "bg-green-50", border: "border-green-200", hover: "hover:bg-green-100" },
      "Diagnosis and Coding": { bg: "bg-blue-50", border: "border-blue-200", hover: "hover:bg-blue-100" },
      "Functional Limitations": { bg: "bg-red-50", border: "border-red-200", hover: "hover:bg-red-100" },
      "Mental Status": { bg: "bg-purple-50", border: "border-purple-200", hover: "hover:bg-purple-100" },
      "Activities Permitted": { bg: "bg-blue-50", border: "border-blue-200", hover: "hover:bg-blue-100" },
      "Physician Orders and Certification": { bg: "bg-green-50", border: "border-green-200", hover: "hover:bg-green-100" },
      "Home Health Certification": { bg: "bg-green-50", border: "border-green-200", hover: "hover:bg-green-100" },
      "Patient History": { bg: "bg-gray-50", border: "border-gray-200", hover: "hover:bg-gray-100" },
      "Pediatric Information": { bg: "bg-purple-50", border: "border-purple-200", hover: "hover:bg-purple-100" },
      "Pediatric Motor Development": { bg: "bg-purple-50", border: "border-purple-200", hover: "hover:bg-purple-100" },
      "Pediatric PDN Physical Therapy": { bg: "bg-red-50", border: "border-red-200", hover: "hover:bg-red-100" },
      "Pain Assessment": { bg: "bg-orange-50", border: "border-orange-200", hover: "hover:bg-orange-100" },
      "Range of Motion": { bg: "bg-blue-50", border: "border-blue-200", hover: "hover:bg-blue-100" },
      "Strength Assessment": { bg: "bg-yellow-50", border: "border-yellow-200", hover: "hover:bg-yellow-100" },
      "Balance & Coordination": { bg: "bg-teal-50", border: "border-teal-200", hover: "hover:bg-teal-100" },
      "Gait Analysis": { bg: "bg-indigo-50", border: "border-indigo-200", hover: "hover:bg-indigo-100" },
      "Functional Mobility": { bg: "bg-blue-50", border: "border-blue-200", hover: "hover:bg-blue-100" },
      "Equipment & Assistive Devices": { bg: "bg-gray-50", border: "border-gray-200", hover: "hover:bg-gray-100" },
      "Home Safety Assessment": { bg: "bg-orange-50", border: "border-orange-200", hover: "hover:bg-orange-100" },
      "Treatment Plan": { bg: "bg-green-50", border: "border-green-200", hover: "hover:bg-green-100" },
      "PT Attestation": { bg: "bg-blue-50", border: "border-blue-200", hover: "hover:bg-blue-100" }
    };
    return colorMap[categoryName] || { bg: "bg-white", border: "border-gray-200", hover: "hover:bg-gray-50" };
  };

  const getCategoryIcon = (categoryName) => {
    const icons = {
      "Recertification Summary": <RefreshCw size={18} className="mr-2 text-amber-600" />,
      "Discharge Summary": <LogOut size={18} className="mr-2 text-green-600" />,
      "Diagnosis and Coding": <FileBarChart size={18} className="mr-2 text-blue-600" />,
      "Functional Limitations": <AlertTriangle size={18} className="mr-2 text-red-600" />,
      "Mental Status": <Activity size={18} className="mr-2 text-purple-600" />,
      "Activities Permitted": <CheckCircle size={18} className="mr-2 text-blue-600" />,
      "Physician Orders and Certification": <FileText size={18} className="mr-2 text-green-600" />,
      "Home Health Certification": <Shield size={18} className="mr-2 text-green-600" />,
      "Patient History": <FileText size={18} className="mr-2 text-gray-700" />,
      "Pediatric Information": <Baby size={18} className="mr-2 text-purple-600" />,
      "Pediatric Motor Development": <Footprints size={18} className="mr-2 text-purple-600" />,
      "Pediatric PDN Physical Therapy": <Heart size={18} className="mr-2 text-red-600" />,
      "Pain Assessment": <AlertTriangle size={18} className="mr-2 text-orange-600" />,
      "Range of Motion": <Move size={18} className="mr-2 text-blue-600" />,
      "Strength Assessment": <Zap size={18} className="mr-2 text-yellow-600" />,
      "Balance & Coordination": <Gauge size={18} className="mr-2 text-teal-600" />,
      "Gait Analysis": <Footprints size={18} className="mr-2 text-indigo-600" />,
      "Functional Mobility": <Activity size={18} className="mr-2 text-blue-700" />,
      "Equipment & Assistive Devices": <Target size={18} className="mr-2 text-gray-700" />,
      "Home Safety Assessment": <Home size={18} className="mr-2 text-orange-600" />,
      "Treatment Plan": <Target size={18} className="mr-2 text-green-600" />,
      "PT Attestation": <CheckCircle size={18} className="mr-2 text-blue-600" />
    };
    return icons[categoryName] || <FileText size={18} className="mr-2 text-gray-700" />;
  };

  const validateForm = () => {
    const errors = [];
    const categories = getAssessmentCategories();
    
    // Check required fields
    categories.forEach(section => {
      section.fields.forEach(field => {
        if (shouldShowField(field) && field.required && !formData.categories?.[`${section.name}-${field.name}`]) {
          errors.push(`${field.name} is required`);
        }
      });
    });
    
    // NEW VALIDATION RULES:
    
    // 1. Physician signature timing (if verbal order received)
    const verbalOrderDate = formData.categories?.['Physician Orders and Certification-Verbal Order Date'];
    const physicianReviewedDate = formData.categories?.['Physician Orders and Certification-Physician Received/Reviewed POC Date'];
    
    if (verbalOrderDate && physicianReviewedDate) {
      const daysDiff = Math.floor((new Date(physicianReviewedDate).getTime() - new Date(verbalOrderDate).getTime()) / (1000 * 60 * 60 * 24));
      if (daysDiff > 14) {
        errors.push('WARNING: Physician must receive/review POC within 14 days of verbal order (CMS requirement)');
      }
    }
    
    // 2. Medical necessity minimum length
    const clinicalComplexity = formData.categories?.['Diagnosis and Coding-Medical Necessity - Clinical Complexity'] || '';
    const skilledCare = formData.categories?.['Diagnosis and Coding-Medical Necessity - Skilled Care Requirements'] || '';
    const functionalImpact = formData.categories?.['Diagnosis and Coding-Medical Necessity - Functional Impact'] || '';
    const expectedOutcomes = formData.categories?.['Diagnosis and Coding-Medical Necessity - Expected Outcomes'] || '';
    
    const totalMedNecLength = clinicalComplexity.length + skilledCare.length + functionalImpact.length + expectedOutcomes.length;
    
    if (totalMedNecLength < 500 && formConfig.assessmentType !== 'discharge') {
      errors.push('Medical necessity documentation too brief - CMS audits require detailed justification (minimum 500 characters across all sections)');
    }
    
    // 3. Homebound criteria
    const homeboundCriteria = formData.categories?.['Home Health Certification-Homebound Status Criteria (Check all that apply)'];
    if (formConfig.settingType === 'home-health' && formConfig.assessmentType !== 'discharge') {
      if (!homeboundCriteria || homeboundCriteria.length === 0) {
        errors.push('Must select at least one homebound criterion');
      }
    }
    
    // 4. Functional limitations
    const funcLimitations = formData.categories?.['Functional Limitations-Functional Limitations Checkboxes'];
    if (formConfig.assessmentType !== 'discharge') {
      if (!funcLimitations || funcLimitations.length === 0) {
        errors.push('Must select at least one functional limitation');
      }
    }
    
    // Phase 19 F23: test results or the reason none was given (initial / recertification)
    errors.push(...therapyTestErrors(formConfig.assessmentType, pickTherapyTestFields(formData)));

    return errors;
  };

  // Payload passed to onSave (Save, Submit and autosave all use it)
  const buildSaveData = () => ({
    ...formData,
    formConfig: withPediatricFirstConfig(formConfig),
    status: 'Draft',
    lastUpdated: new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true })
  });

  // Save keeps a DRAFT and never blocks on required fields; Submit runs validate() (Phase 4)
  const handleSave = () => {
    onSave(buildSaveData());
  };


  // Phase 19 F23: standardized tests are saved at the top level of data, next to categories
  const handleTherapyTestChange = (patch: Partial<TherapyTestFieldsDto>) => {
    setFormData(prev => ({ ...prev, ...patch }));
  };
  const showTests = formConfig.assessmentType !== 'discharge';
  const renderedCategories = getAssessmentCategories();
  // Shown just before the first section after the developmental section (priority 3.5)
  const testsAnchor = showTests ? renderedCategories.findIndex(c => c.priority > 3.5) : -1;
  const testsSection = showTests ? (
    <StandardizedTestSection
      discipline="PT"
      value={pickTherapyTestFields(formData)}
      onChange={handleTherapyTestChange}
      readOnly={isReadOnly}
      legacy={legacyEntries(formData.categories, LEGACY_TEST_KEYS)}
      collapsed={collapsedSections['Standardized Tests'] === true}
      onToggle={() => toggleSection('Standardized Tests')}
    />
  ) : null;

  useImperativeHandle(ref, () => ({
    triggerSave: () => handleSave(),
    validate: () => validateForm(),
    getSaveData: () => buildSaveData(),
    getSnapshot: () => JSON.stringify(formData),
  }));

  return (
    <div className="space-y-6">
      <div className="bg-white text-left">

        {/* Header */}
        <div className="border-b border-gray-200 pb-4 mb-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => toggleAllSections(!Object.values(collapsedSections).every(v => v))}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md border border-gray-300 transition-colors"
            >
              {Object.values(collapsedSections).every(v => v) ? (
                <>
                  <ChevronsDown size={16} />
                  <span>Expand All</span>
                </>
              ) : (
                <>
                  <ChevronsUp size={16} />
                  <span>Collapse All</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Assessment Type and Configuration - Collapsible */}
        <div className="bg-gray-50 rounded-lg mb-6 border border-gray-200">
          {/* Collapsible Header */}
          <div
            className="flex items-center justify-between cursor-pointer p-4 hover:bg-gray-100 rounded-t-lg transition-colors"
            onClick={() => setConfigSectionCollapsed(!configSectionCollapsed)}
          >
            <div className="flex items-center gap-3">
              <FileEdit size={18} className="text-blue-600" />
              <div>
                <h4 className="text-sm font-medium text-gray-700">Evaluation Configuration</h4>
                {configSectionCollapsed && formConfig.assessmentType && ASSESSMENT_TYPES[formConfig.assessmentType] && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {ASSESSMENT_TYPES[formConfig.assessmentType].label} • {patientTypeLabel(formConfig.patientType)}
                  </p>
                )}
              </div>
            </div>
            <div>
              {configSectionCollapsed ? <ChevronDown size={20} className="text-gray-500" /> : <ChevronUp size={20} className="text-gray-500" />}
            </div>
          </div>

          {/* Collapsible Content */}
          <div className={configSectionCollapsed ? 'hidden' : 'p-4 pt-0'}>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Evaluation Type</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            {(Object.keys(ASSESSMENT_TYPES) as AssessmentType[]).map((type) => {
              const config = ASSESSMENT_TYPES[type];
              const isSelected = formConfig.assessmentType === type;
              const colorClasses = {
                blue: isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300 bg-white',
                amber: isSelected ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-gray-300 bg-white',
                green: isSelected ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300 bg-white'
              };
              const textColorClasses = {
                blue: isSelected ? 'text-blue-600' : 'text-gray-500',
                amber: isSelected ? 'text-amber-600' : 'text-gray-500',
                green: isSelected ? 'text-green-600' : 'text-gray-500'
              };
              
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => (!editingId || assessmentStatus === 'DRAFT') && !isReadOnly && handleConfigChange('assessmentType', type)}
                  disabled={(!!editingId && assessmentStatus !== 'DRAFT') || isReadOnly}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${colorClasses[config.color]} ${editingId && assessmentStatus !== 'DRAFT' ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <div className="flex items-center mb-1">
                    <span className={`mr-2 ${textColorClasses[config.color]}`}>{config.icon}</span>
                    <span className={`font-medium text-sm ${isSelected ? `text-${config.color}-900` : 'text-gray-700'}`}>
                      {config.label}
                    </span>
                  </div>
                  <p className={`text-xs ${isSelected ? `text-${config.color}-700` : 'text-gray-500'}`}>
                    {config.description}
                  </p>
                </button>
              );
            })}
          </div>

          {/* Assessment Type Notice */}
          {formConfig.assessmentType === 'recertification' && (
            <div className="p-3 rounded-lg flex items-start mb-4 bg-amber-50 border border-amber-200">
              <RefreshCw size={18} className="text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <strong>Recertification Mode:</strong> Birth history and baseline fields are hidden. 
                Focus on documenting progress toward goals and justifying continued PT services.
              </div>
            </div>
          )}
          
          {formConfig.assessmentType === 'discharge' && (
            <div className="p-3 rounded-lg flex items-start mb-4 bg-green-50 border border-green-200">
              <LogOut size={18} className="text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-green-800">
                <strong>Discharge Mode:</strong> Treatment planning fields are hidden. 
                Focus on documenting outcomes, goal achievement, and recommendations.
              </div>
            </div>
          )}

          {/* Form Configuration */}
          <h4 className="text-sm font-medium text-gray-700 mb-3 mt-4">Evaluation Configuration</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
              <input type="date" className="w-full p-2 border border-gray-300 rounded text-base"
                disabled={isReadOnly}
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})} />
            </div>
            <AdultTransitionToggle disabled={isReadOnly} patientType={formConfig.patientType}
              onChange={(patientType) => handleConfigChange('patientType', patientType)} />
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Provider</label>
              <input type="text" className="w-full p-2 border border-gray-300 rounded text-base"
                disabled={isReadOnly}
                value={formData.provider}
                onChange={(e) => setFormData({...formData, provider: e.target.value})} />
            </div>
          </div>
          
          {/* Pediatric PDN Notice */}
          {formConfig.patientType === 'pediatric' && formConfig.settingType === 'home-health' && (
            <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-start">
              <Baby size={18} className="text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-purple-800">
                <strong>Pediatric Home Health PT:</strong> Includes specialized developmental assessment and PDN-specific documentation for Texas Medicaid.
              </div>
            </div>
          )}
          </div>
        </div>

        {/* Main form content - categories */}
        {renderedCategories.map((category, categoryIndex) => {
          const visibleFields = category.fields.filter(field => shouldShowField(field));
          if (visibleFields.length === 0) return categoryIndex === testsAnchor ? <div key={categoryIndex}>{testsSection}</div> : null;
          
          const colors = getCategoryColorClasses(category.name);
          
          return (
            <div key={categoryIndex} className="mb-6">
              {categoryIndex === testsAnchor && testsSection}
              <div className={`rounded-lg border ${colors.border} shadow-sm ${colors.bg}`}>
                <div className={`flex items-center justify-between cursor-pointer p-4 ${colors.hover} rounded-t-lg transition-colors`} onClick={() => toggleSection(category.name)}>
                  <div className="flex items-center">
                    {getCategoryIcon(category.name)}
                    <h4 className="text-base font-medium text-gray-900">{category.name}</h4>
                    {category.required && <span className="ml-2 text-xs text-red-600 font-medium">Required</span>}
                  </div>
                  <div>
                    {collapsedSections[category.name] ? <ChevronDown size={16} className="text-gray-500" /> : <ChevronUp size={16} className="text-gray-500" />}
                  </div>
                </div>
                
                {category.description && !collapsedSections[category.name] && (
                  <p className="text-sm text-gray-600 px-4 pb-2 border-b border-gray-100">{category.description}</p>
                )}

                <div className={`p-4 ${collapsedSections[category.name] ? 'hidden' : 'block'}`}>
                {visibleFields.map((field, fieldIndex) => {
                  const fieldKey = `${category.name}-${field.name}`;
                  const placeholder = getPlaceholder(field);
                  
                  return (
                    <div key={fieldIndex} className="mb-4 min-w-0">
                      <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        {field.name}
                        {field.required && <span className="ml-1 text-red-600">*</span>}
                      </label>
                      
                      {field.type === 'select' && field.name === 'Primary ICD-10 Code' && (
                        <div>
                          <ICDSelect
                            value={formData.categories?.[fieldKey] || ''}
                            onChange={(val) => handleInputChange(fieldKey, val)}
                            placeholder={`Select ${field.name}...`}
                            className={`min-w-0 ${field.required && !formData.categories?.[fieldKey] ? 'ring-1 ring-red-300' : ''}`}
                            disabled={isReadOnly}
                          />
                        </div>
                      )}
                      {field.type === 'select' && field.name !== 'Primary ICD-10 Code' && (
                        <select
                          disabled={isReadOnly}
                          className={`w-full p-2 border rounded text-base ${field.required && !formData.categories?.[fieldKey] ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                          value={formData.categories?.[fieldKey] || ''}
                          onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                        >
                          <option value="">Select {field.name}...</option>
                          {field.options?.map((option, idx) => <option key={idx} value={option}>{option}</option>)}
                        </select>
                      )}
                      
                      {field.type === 'date' && (
                        <input type="date"
                          disabled={isReadOnly}
                          className={`w-full p-2 border rounded text-base ${field.required && !formData.categories?.[fieldKey] ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                          value={formData.categories?.[fieldKey] || ''}
                          onChange={(e) => handleInputChange(fieldKey, e.target.value)} />
                      )}
                      
                      {field.type === 'textarea' && (
                        <textarea rows={3}
                          disabled={isReadOnly}
                          className={`w-full p-2 border rounded text-base ${field.required && !formData.categories?.[fieldKey] ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                          value={formData.categories?.[fieldKey] || ''}
                          onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                          placeholder={placeholder} />
                      )}
                      
                      {field.type === 'text' && (
                        <input type="text"
                          disabled={isReadOnly}
                          className={`w-full p-2 border rounded ${field.readonly ? 'bg-gray-50 text-gray-600' : ''} ${field.required && !formData.categories?.[fieldKey] ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                          value={formData.categories?.[fieldKey] || ''}
                          onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                          placeholder={placeholder}
                          readOnly={field.readonly} />
                      )}

                      {field.type === 'number' && (
                        <input type="number"
                          disabled={isReadOnly}
                          className={`w-full p-2 border rounded ${field.readonly ? 'bg-gray-50 text-gray-600' : ''} ${field.required && !formData.categories?.[fieldKey] ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                          value={formData.categories?.[fieldKey] || ''}
                          onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                          placeholder={placeholder}
                          readOnly={field.readonly}
                          min={field.min}
                          max={field.max} />
                      )}

                      {field.type === 'checkbox-group' && (
                        <div className="space-y-2">
                          {field.options?.map((option, idx) => {
                            const currentValues = formData.categories?.[fieldKey] ? 
                              (Array.isArray(formData.categories[fieldKey]) ? formData.categories[fieldKey] : formData.categories[fieldKey].split(',')) : [];
                            return (
                              <label key={idx} className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  className="rounded border-gray-300"
                                  checked={currentValues.includes(option)}
                                  onChange={(e) => {
                                    const newValues = e.target.checked
                                      ? [...currentValues, option]
                                      : currentValues.filter(v => v !== option);
                                    handleInputChange(fieldKey, newValues);
                                  }}
                                />
                                <span className="text-sm text-gray-700">{option}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}

                      {field.type === 'custom-icd10-list' && (
                        <div className="space-y-3">
                          <div className="flex flex-col sm:flex-row gap-2 min-w-0">
                            <ICDSelect
                              exclude={secondaryICD10Codes}
                              value={selectedSecondaryICD10}
                              onChange={(val) => setSelectedSecondaryICD10(val)}
                              placeholder="Select ICD-10 code..."
                              className="flex-1 w-full"
                              disabled={isReadOnly}
                            />
                            <PrimaryButton
                              type="button"
                              onClick={handleAddSecondaryICD10}
                              disabled={!selectedSecondaryICD10 || isReadOnly}
                              className="whitespace-nowrap w-full sm:w-auto"
                            >
                              Add ICD Code
                            </PrimaryButton>
                          </div>
                          {secondaryICD10Codes.length > 0 && (
                            <div className="border border-gray-200 rounded p-3 bg-gray-50">
                              <p className="text-sm font-medium text-gray-700 mb-2">Added Secondary Diagnoses:</p>
                              <ul className="space-y-2">
                                {secondaryICD10Codes.map((code, idx) => (
                                  <li key={idx} className="flex items-center justify-between bg-white p-2 rounded border border-gray-200">
                                    <span className="text-sm">{code}</span>
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveSecondaryICD10(code)}
                                      disabled={isReadOnly}
                                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                                    >
                                      Remove
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            </div>
          );
        })}
        {testsAnchor === -1 && testsSection}
        
      </div>
    </div>
  );
  }
);
PTAssessmentForm.displayName = 'PTAssessmentForm';

export default PTAssessmentForm;
